import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Button,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Badge,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  Checkbox,
  useDisclosure,
  Radio,
  RadioGroup,
  Stack,
} from '@chakra-ui/react';
import { FiEdit2, FiTrash2, FiPlus, FiUsers, FiWifi } from 'react-icons/fi';

const API_BASE = '/api/v1';

function SuperAdmin() {
  const toast = useToast();

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ username: '', email: '', password: '', is_superuser: false });

  const { isOpen: isUserModalOpen, onOpen: onUserModalOpen, onClose: onUserModalClose } = useDisclosure();
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();
  const [userToDelete, setUserToDelete] = useState(null);

  const [turnForm, setTurnForm] = useState({
    provider: 'coturn', custom_urls: '', custom_username: '', custom_credential: '',
  });
  const [loadingTurn, setLoadingTurn] = useState(true);
  const [savingTurn, setSavingTurn] = useState(false);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('meet_token')}`,
  });

  useEffect(() => {
    fetch(`${API_BASE}/admin-users/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => {
        setUsers(Array.isArray(data) ? data : []);
        setLoadingUsers(false);
      })
      .catch(() => setLoadingUsers(false));

    fetch(`${API_BASE}/admin-users/me/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => setCurrentUser(data))
      .catch(() => {});

    fetch(`${API_BASE}/admin-turn-settings`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => {
        setTurnForm({
          provider: data.provider || 'coturn',
          custom_urls: Array.isArray(data.custom_urls) ? data.custom_urls.join(', ') : '',
          custom_username: data.custom_username || '',
          custom_credential: data.custom_credential || '',
        });
        setLoadingTurn(false);
      })
      .catch(() => setLoadingTurn(false));
  }, []);

  const handleSaveTurnSettings = async () => {
    setSavingTurn(true);
    try {
      const res = await fetch(`${API_BASE}/admin-turn-settings`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          provider: turnForm.provider,
          custom_urls: turnForm.custom_urls.split(',').map(u => u.trim()).filter(Boolean),
          custom_username: turnForm.custom_username,
          custom_credential: turnForm.custom_credential,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        toast({ title: 'TURN settings saved', status: 'success', duration: 3000 });
      } else {
        toast({ title: data.error || 'Error saving TURN settings', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error saving TURN settings', status: 'error', duration: 3000 });
    }
    setSavingTurn(false);
  };

  const openAddUser = () => {
    setEditingUser(null);
    setUserForm({ username: '', email: '', password: '', is_superuser: false });
    onUserModalOpen();
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      username: user.username,
      email: user.email || '',
      password: '',
      is_superuser: user.is_superuser,
      is_active: user.is_active,
    });
    onUserModalOpen();
  };

  const handleSaveUser = async () => {
    const method = editingUser ? 'PUT' : 'POST';
    const url = editingUser ? `${API_BASE}/admin-users/${editingUser.id}/` : `${API_BASE}/admin-users/`;

    const payload = { ...userForm };
    if (!payload.password) delete payload.password;

    try {
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        if (editingUser) {
          setUsers(users.map(u => u.id === editingUser.id ? data : u));
        } else {
          setUsers([...users, data]);
        }
        toast({ title: editingUser ? 'User updated' : 'User created', status: 'success', duration: 3000 });
        onUserModalClose();
      } else {
        toast({ title: data.error || 'Error saving user', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error saving user', status: 'error', duration: 3000 });
    }
  };

  const confirmDeleteUser = (user) => {
    setUserToDelete(user);
    onDeleteModalOpen();
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      const res = await fetch(`${API_BASE}/admin-users/${userToDelete.id}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (res.ok || data.success) {
        setUsers(users.filter(u => u.id !== userToDelete.id));
        toast({ title: 'User deleted', status: 'success', duration: 3000 });
      } else {
        toast({ title: data.error || 'Error deleting user', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error deleting user', status: 'error', duration: 3000 });
    }
    onDeleteModalClose();
    setUserToDelete(null);
  };

  return (
    <Box>
      <Heading size="lg" mb={6}>Super Admin</Heading>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
        <HStack justify="space-between" mb={6}>
          <HStack>
            <FiUsers />
            <Heading size="md">Admin Users</Heading>
          </HStack>
          <Button leftIcon={<FiPlus />} colorScheme="teal" onClick={openAddUser}>
            Add User
          </Button>
        </HStack>

        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Username</Th>
              <Th>Email</Th>
              <Th>Status</Th>
              <Th>Role</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {users.map(user => (
              <Tr key={user.id}>
                <Td fontWeight="medium">{user.username}</Td>
                <Td>{user.email || '-'}</Td>
                <Td>
                  <Badge colorScheme={user.is_active ? 'green' : 'red'}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </Td>
                <Td>
                  <Badge colorScheme={user.is_superuser ? 'teal' : 'gray'}>
                    {user.is_superuser ? 'Super Admin' : 'Admin'}
                  </Badge>
                </Td>
                <Td>
                  <HStack spacing={2}>
                    <IconButton
                      icon={<FiEdit2 />}
                      size="sm"
                      colorScheme="blue"
                      variant="ghost"
                      onClick={() => openEditUser(user)}
                      aria-label="Edit user"
                    />
                    <IconButton
                      icon={<FiTrash2 />}
                      size="sm"
                      colorScheme="red"
                      variant="ghost"
                      onClick={() => confirmDeleteUser(user)}
                      aria-label="Delete user"
                      isDisabled={currentUser && currentUser.id === user.id}
                    />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>

        {users.length === 0 && !loadingUsers && (
          <Text textAlign="center" py={8} color="gray.500">
            No admin users found
          </Text>
        )}
      </Box>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md" mt={6}>
        <HStack mb={6}>
          <FiWifi />
          <Heading size="md">TURN Server</Heading>
        </HStack>

        {!loadingTurn && (
          <VStack spacing={4} align="stretch" maxW="lg">
            <Text fontSize="sm" color="gray.600">
              Controls which TURN server call participants' browsers fall back to when a
              direct/STUN connection isn't possible. Applies to new joins immediately.
            </Text>

            <FormControl>
              <FormLabel>Provider</FormLabel>
              <RadioGroup
                value={turnForm.provider}
                onChange={(v) => setTurnForm({ ...turnForm, provider: v })}
              >
                <Stack spacing={2}>
                  <Radio value="coturn" colorScheme="teal">Self-hosted (coturn) — default</Radio>
                  <Radio value="custom" colorScheme="teal">Custom provider</Radio>
                </Stack>
              </RadioGroup>
            </FormControl>

            {turnForm.provider === 'custom' && (
              <>
                <FormControl>
                  <FormLabel>TURN URL(s)</FormLabel>
                  <Input
                    value={turnForm.custom_urls}
                    onChange={(e) => setTurnForm({ ...turnForm, custom_urls: e.target.value })}
                    placeholder="turn:turn.example.com:3478, turns:turn.example.com:5349"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Username</FormLabel>
                  <Input
                    value={turnForm.custom_username}
                    onChange={(e) => setTurnForm({ ...turnForm, custom_username: e.target.value })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Credential</FormLabel>
                  <Input
                    type="password"
                    value={turnForm.custom_credential}
                    onChange={(e) => setTurnForm({ ...turnForm, custom_credential: e.target.value })}
                  />
                </FormControl>
              </>
            )}

            <Box>
              <Button colorScheme="teal" onClick={handleSaveTurnSettings} isLoading={savingTurn}>
                Save TURN Settings
              </Button>
            </Box>
          </VStack>
        )}
      </Box>

      {/* User Modal */}
      <Modal isOpen={isUserModalOpen} onClose={onUserModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingUser ? 'Edit User' : 'Add User'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Username</FormLabel>
                <Input
                  value={userForm.username}
                  onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                  placeholder="Enter username"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  placeholder="Enter email"
                />
              </FormControl>

              <FormControl isRequired={!editingUser}>
                <FormLabel>{editingUser ? 'New Password (leave blank to keep current)' : 'Password'}</FormLabel>
                <Input
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  placeholder={editingUser ? 'Leave blank to keep current' : 'Enter password'}
                />
              </FormControl>

              <FormControl>
                <Checkbox
                  isChecked={userForm.is_superuser}
                  onChange={(e) => setUserForm({ ...userForm, is_superuser: e.target.checked })}
                  colorScheme="teal"
                >
                  Super Admin (full access)
                </Checkbox>
              </FormControl>

              {editingUser && (
                <FormControl>
                  <Checkbox
                    isChecked={userForm.is_active}
                    onChange={(e) => setUserForm({ ...userForm, is_active: e.target.checked })}
                    colorScheme="teal"
                  >
                    Active
                  </Checkbox>
                </FormControl>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onUserModalClose}>Cancel</Button>
            <Button colorScheme="teal" onClick={handleSaveUser}>
              {editingUser ? 'Save Changes' : 'Create User'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete User</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to delete user <strong>{userToDelete?.username}</strong>?
            This action cannot be undone.
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteModalClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDeleteUser}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default SuperAdmin;
